'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { EERIE } from 'HubStyleTokens/colors';
import { WEB_FONT_DEMI_BOLD_WEIGHT } from 'HubStyleTokens/misc';
import UIFlex from 'UIComponents/layout/UIFlex';
import H1 from 'UIComponents/elements/headings/H1';
import Big from 'UIComponents/elements/Big';
import Small from 'UIComponents/elements/Small';
import UICardSection from 'UIComponents/card/UICardSection';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import Rating from 'marketplace-ui-reviews/components/Rating';
import PartnerTypeIcon from 'marketplace-ui-solutions-core/components/PartnerTypeIcon';
import ProfileListing from 'ecosystem-marketplace-core/records/ProfileListing';
import OfficeLocation from 'ecosystem-marketplace-core/records/OfficeLocation';
import { getProfileTierLabelI18nKey } from 'marketplace-ui-solutions-core/utils/i18n';
import * as ProfileTierLabelKeys from 'marketplace-ui-solutions-core/constants/ProfileTierLabelKey';
import { getGoogleMapsUrl } from '../../utils/urls';
import ProfileCardWrapper from './ProfileCardWrapper';
const I18N_PATH = 'ecosystem-marketplace-solutions-ui.profile.contact';
const TooltipHeading = styled(Big).withConfig({
  displayName: "ContactInfoCard__TooltipHeading",
  componentId: "pl1o37-0"
})(["display:block;margin-bottom:8px;font-weight:600;"]);

const ContactInfoCard = ({
  onTrackInteraction,
  profileListing,
  scrollToTabSection
}) => {
  const {
    locations,
    name,
    partnerType,
    reviewSummary,
    tier,
    websiteUrl
  } = profileListing;
  const trackOfficeLocationClick = useCallback(location => {
    onTrackInteraction('click view office location', {
      location
    });
  }, [onTrackInteraction]);
  const trackRemoteOfficeLocationClick = useCallback(() => {
    trackOfficeLocationClick('remote');
  }, [trackOfficeLocationClick]);
  const trackPhysicalOfficeLocationClick = useCallback(() => {
    trackOfficeLocationClick('physical');
  }, [trackOfficeLocationClick]);
  const renderedLocations = useMemo(() => {
    // All locations must be of the same type, either physical or remote
    const isRemote = locations.size && locations.first().get('remote');

    if (isRemote) {
      return /*#__PURE__*/_jsxs(UIFlex, {
        justify: "center",
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "location",
          color: EERIE,
          size: 16,
          className: "m-right-1"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.remote`
        }), /*#__PURE__*/_jsx(UIHelpIcon, {
          "aria-label": unescapedText(`${I18N_PATH}.remoteTooltipAccessibleName`),
          className: "m-left-1",
          role: "tooltip",
          tooltipPlacement: "right",
          tooltipProps: {
            maxWidth: 250
          },
          title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: `${I18N_PATH}.remoteTooltip_jsx`,
            elements: {
              Heading: TooltipHeading,
              List: UIList
            },
            options: {
              profileName: name,
              locations: locations.map((loc, index) => {
                const officeLocation = OfficeLocation.from(loc);
                return /*#__PURE__*/_jsx(UILink, {
                  external: true,
                  href: getGoogleMapsUrl(officeLocation.buildFullAddress()),
                  onClick: trackRemoteOfficeLocationClick,
                  children: officeLocation.buildFullCity()
                }, index);
              })
            }
          })
        })]
      });
    }

    return locations.map((location, index) => {
      const officeLocation = OfficeLocation.from(location);
      return /*#__PURE__*/_jsxs(UIFlex, {
        justify: "center",
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "location",
          color: EERIE,
          size: 16,
          className: "m-right-1"
        }), /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: getGoogleMapsUrl(officeLocation.buildFullAddress()),
          onClick: trackPhysicalOfficeLocationClick,
          children: officeLocation.buildFullCity()
        })]
      }, index);
    });
  }, [locations, name, trackPhysicalOfficeLocationClick, trackRemoteOfficeLocationClick]);
  const formattedWebsiteUrl = useMemo(() => {
    if (websiteUrl && !/https?:\/\//i.test(websiteUrl)) {
      return `http://${websiteUrl}`;
    }

    return websiteUrl;
  }, [websiteUrl]);
  const renderedTierLabel = useMemo(() => {
    const profileTierLabelI18nKey = getProfileTierLabelI18nKey(tier, partnerType, ProfileTierLabelKeys.TITLE);

    if (!profileTierLabelI18nKey) {
      return null;
    }

    return /*#__PURE__*/_jsx(UIMediaObject, {
      align: "center",
      spacing: "extra-small",
      itemLeft: /*#__PURE__*/_jsx(PartnerTypeIcon, {
        bordered: true,
        partnerType: partnerType,
        tier: tier,
        size: 'small',
        className: "m-bottom-1"
      }),
      children: /*#__PURE__*/_jsx(Small, {
        style: {
          fontWeight: WEB_FONT_DEMI_BOLD_WEIGHT
        },
        className: "m-top-2 uppercase",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: profileTierLabelI18nKey
        })
      })
    });
  }, [partnerType, tier]);
  return /*#__PURE__*/_jsx(ProfileCardWrapper, {
    children: /*#__PURE__*/_jsxs(UICardSection, {
      className: "flex-column align-center",
      children: [renderedTierLabel, /*#__PURE__*/_jsx(H1, {
        className: "text-center",
        children: name
      }), /*#__PURE__*/_jsx(UIFlex, {
        justify: "center",
        children: reviewSummary.get('overallRating') ? /*#__PURE__*/_jsx(Rating, {
          rating: reviewSummary.get('overallRating'),
          justify: "center",
          label: /*#__PURE__*/_jsx(UILink, {
            onClick: () => scrollToTabSection('reviews'),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ecosystem-marketplace-solutions-ui.profile.reviews.reviewCount`,
              options: {
                count: reviewSummary.get('reviewCount')
              }
            })
          }),
          labelSize: "medium"
        }) : null
      }), /*#__PURE__*/_jsx(UIFlex, {
        direction: "column",
        align: "center",
        justify: "center",
        className: "m-top-2",
        children: renderedLocations
      }), /*#__PURE__*/_jsxs(UIFlex, {
        justify: "center",
        className: "m-top-2",
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "language",
          color: EERIE,
          size: 16,
          className: "m-right-1"
        }), /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: formattedWebsiteUrl,
          onClick: () => onTrackInteraction('click visit agency website'),
          rel: "noopener",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_PATH}.website`
          })
        })]
      }), /*#__PURE__*/_jsx(UIButton, {
        className: "m-top-6",
        external: true,
        href: formattedWebsiteUrl,
        onClick: () => onTrackInteraction('click contact agency'),
        rel: "noopener",
        use: "tertiary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.business`
        })
      })]
    })
  });
};

ContactInfoCard.propTypes = {
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  scrollToTabSection: PropTypes.func.isRequired
};
export default ContactInfoCard;